import React, {useEffect, useMemo, useState} from "react";
import {getAllDocs} from "./firebase";
import {TransactionModal} from "./TransactionModal";

export function AdminMode() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(null);

  useEffect(() => {
    getAllDocs().then(setUsers)
  }, []);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      const text = search.toLowerCase();
      return (
        u.email?.toLowerCase().includes(text) ||
        u.twitter_display_name?.toLowerCase().includes(text.startsWith('@') ? text.substring(1) : text) ||
        u.twitter_screen_name?.toLowerCase().includes(text) ||
        u.wallet_address?.toLowerCase().includes(text)
      );
    });
  }, [users, search])

  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleCopyAddress = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      // Hide the "Copied!" message after 2 seconds
      setTimeout(() => {
        setCopiedAddress(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy address: ', err);
    }
  };

  return <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-yellow-300 to-amber-400">
    {/* Header */}
    <div className="bg-black text-yellow-400 py-4 sm:py-6 shadow-lg border-b-4 border-yellow-400">
      <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-between items-center">
        <a
          href="/"
          className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-xl transition transform hover:scale-105 hover:bg-yellow-400 hover:text-black cursor-pointer"
        >
          {/* Emoji automatically resizes */}
          <div className="text-3xl sm:text-4xl md:text-5xl">🥕🪵</div>
          {/* Title scales and wraps on small screens */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold whitespace-nowrap">
            Carrot&nbsp;&amp;&nbsp;Stick
          </h1>
        </a>
      </div>
    </div>    {/* Welcome Section */}
      <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="bg-black rounded-2xl shadow-2xl p-8 mb-6 border-4 border-yellow-400">
        <div className="flex justify-center mb-6">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email, Twitter, or wallet..."
            className="w-full md:w-1/2 px-4 py-2 bg-gray-900 text-yellow-200 border border-yellow-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-300 placeholder-yellow-500"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border-collapse">
            <thead>
            <tr className="bg-yellow-400 text-black">
              <th className="px-4 py-3 text-left font-semibold rounded-tl-lg">
                Email
              </th>
              <th className="px-4 py-3 text-left font-semibold">
                Twitter Display Name
              </th>
              <th className="px-4 py-3 text-left font-semibold">
                Twitter Screen Name
              </th>
              <th className="px-4 py-3 text-left font-semibold">
                Wallet Address
              </th>
              <th className="px-4 py-3 text-left font-semibold">
                Created At
              </th>
              <th className="px-4 py-3 text-left font-semibold rounded-tr-lg">
                Transactions
              </th>
            </tr>
            </thead>

            <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="text-center py-6 text-yellow-200 italic"
                >
                  {!search.length ? 'Loading users...' : 'No users found'}
                </td>
              </tr>
            ) : (
              filtered.map((user, i) => (
                <tr
                  key={user.uid || i}
                  className={`${
                    i % 2 === 0 ? "bg-gray-900" : "bg-gray-800"
                  } hover:bg-gray-700 transition`}
                >
                  <td className="px-4 py-3 text-yellow-300">{user.email || "-"}</td>
                  <td className="px-4 py-3 text-yellow-300">{user.twitter_display_name || "-"}</td>
                  <td className="px-4 py-3 text-yellow-300">
                    @{user.twitter_screen_name || "-"}
                  </td>
                  <td className="px-4 py-3 font-mono truncate max-w-[200px] text-yellow-300">
                    {user.wallet_address ? (
                      <div className="flex items-center gap-2">
                        <span>{`${user.wallet_address.slice(0, 6)}...${user.wallet_address.slice(-4)}`}</span>
                        <button
                          onClick={() => handleCopyAddress(user.wallet_address)}
                          className="text-yellow-400 hover:text-yellow-300 transition-colors p-1 rounded hover:bg-yellow-400/10 text-xs"
                          title="Copy full address"
                        >
                          {copiedAddress === user.wallet_address ? 'Copied!' : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-4 py-3 text-yellow-200">
                    {user.created_at
                      ? new Date(user.created_at.seconds * 1000).toLocaleString()
                      : "-"}
                  </td>
                  <td className="px-4 py-3">
                     <button
                       onClick={() => handleOpenModal(user)}
                       className="bg-yellow-400 text-black px-3 py-1 rounded-lg font-semibold hover:bg-yellow-300 transition-colors flex items-center gap-2"
                     >
                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                       </svg>
                       Detail
                     </button>
                  </td>
                </tr>
              ))
            )}
            </tbody>
          </table>
        </div>

      </div>

      {/* Footer Meme */}
      <div className="text-center py-6">
        <p className="text-black italic text-lg font-bold">
          "In Carrot & Stick we trust" 🥕🪵
        </p>
        <p className="text-black text-sm mt-2">
          Disclaimer: This is probably a scam. But a fun one! 😉
        </p>
      </div>
    </div>

    {/* Transaction Modal */}
    <TransactionModal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      user={selectedUser}
    />
  </div>
}
